import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@/components/common/Alert';
import { productService } from '@/services/product.service';
import { categoryService } from '@/services/category.service';
import { CreateProductDto, ProductPack } from '@/types/product.types';
import { CategoryWithSubCategories } from '@/types/category.types';
import '@/styles/dashboard.css';

interface FormErrors {
  name?: string;
  price?: string;
  stock_quantity?: string;
  category?: string;
  image_url?: string;
}

interface CompressOptions {
  maxWidth: number;
  maxHeight: number;
  targetBytes: number;
  minQuality: number;
}

interface CompressResult {
  dataUrl: string;
  originalBytes: number;
  finalBytes: number;
}

export const AjouterProduit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id');
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);

  const [formData, setFormData] = useState<CreateProductDto>({
    name: '',
    description: '',
    more_information: '',
    price: 0,
    reduced_price: undefined,
    taxes_included: true,
    stock_quantity: 0,
    stock_unlimited: false,
    stock_status: 'in-stock',
    featured: false,
    category: '',
    tag: '',
    sku: '',
    image_url: '',
    packs: [],
    status: 'draft',
  });

  const [categories, setCategories] = useState<CategoryWithSubCategories[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string>('');
  const [stockUnlimited, setStockUnlimited] = useState(false);
  const [mainImage, setMainImage] = useState<string>('');
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const additionalImagesInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_INPUT_BYTES = 5 * 1024 * 1024; // 5 Mo par image (après optimisation)

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve((event.target?.result as string) || '');
      reader.onerror = () => reject(new Error('Impossible de lire le fichier image.'));
      reader.readAsDataURL(file);
    });

  const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image invalide ou corrompue.'));
      img.src = src;
    });

  const dataUrlSizeBytes = (dataUrl: string): number => {
    const base64 = dataUrl.split(',')[1] || '';
    const padding = (base64.match(/=+$/)?.[0].length || 0);
    return Math.floor((base64.length * 3) / 4) - padding;
  };

  const compressImageFile = async (file: File, options: CompressOptions): Promise<CompressResult> => {
    const sourceDataUrl = await readFileAsDataUrl(file);
    const image = await loadImage(sourceDataUrl);
    const originalBytes = dataUrlSizeBytes(sourceDataUrl);

    const widthRatio = options.maxWidth / image.width;
    const heightRatio = options.maxHeight / image.height;
    const ratio = Math.min(1, widthRatio, heightRatio);

    let workingWidth = Math.max(1, Math.round(image.width * ratio));
    let workingHeight = Math.max(1, Math.round(image.height * ratio));
    let quality = 0.86;
    const mimeType = file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';

    let bestDataUrl = sourceDataUrl;
    let bestBytes = originalBytes;

    for (let attempt = 0; attempt < 12; attempt++) {
      const canvas = document.createElement('canvas');
      canvas.width = workingWidth;
      canvas.height = workingHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        break;
      }

      // Fond blanc pour garder un rendu propre lors d'une conversion PNG -> JPEG.
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, workingWidth, workingHeight);
      ctx.drawImage(image, 0, 0, workingWidth, workingHeight);

      const currentDataUrl = canvas.toDataURL(mimeType, quality);
      const currentBytes = dataUrlSizeBytes(currentDataUrl);

      if (currentBytes < bestBytes) {
        bestBytes = currentBytes;
        bestDataUrl = currentDataUrl;
      }

      if (currentBytes <= options.targetBytes) {
        return {
          dataUrl: currentDataUrl,
          originalBytes,
          finalBytes: currentBytes,
        };
      }

      if (quality > options.minQuality) {
        quality = Math.max(options.minQuality, quality - 0.08);
      } else {
        workingWidth = Math.max(480, Math.round(workingWidth * 0.85));
        workingHeight = Math.max(480, Math.round(workingHeight * 0.85));
      }
    }

    return {
      dataUrl: bestDataUrl,
      originalBytes,
      finalBytes: bestBytes,
    };
  };

  // Charger les catégories
  useEffect(() => {
    loadCategories();
  }, []);

  // Charger le produit en édition après le chargement des catégories (pour lier catégorie / sous-catégorie)
  useEffect(() => {
    if (!productId) {
      setLoadingProduct(false);
      return;
    }
    setLoadingProduct(true);
    if (loadingCategories) return;
    void loadProduct(parseInt(productId, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadProduct utilise `categories` à jour après loadingCategories
  }, [productId, loadingCategories]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadProduct = async (id: number) => {
    try {
      setLoadingProduct(true);
      const product = await productService.getById(id);
      setFormData({
        name: product.name,
        description: product.description || '',
        more_information: product.more_information || '',
        price: product.price,
        reduced_price: product.reduced_price || undefined,
        taxes_included: product.taxes_included,
        stock_quantity: product.stock_quantity || 0,
        stock_unlimited: product.stock_unlimited,
        stock_status: product.stock_status,
        featured: product.featured,
        category: product.category || '',
        tag: product.tag || '',
        sku: product.sku || '',
        image_url: product.image_url || '',
        packs: product.packs || [],
        status: product.status,
      });
      setStockUnlimited(product.stock_unlimited);
      setMainImage(product.image_url || '');
      
      // Charger les images supplémentaires si elles existent
      if (product.additional_images) {
        if (typeof product.additional_images === 'string') {
          try {
            const parsed = JSON.parse(product.additional_images);
            if (Array.isArray(parsed)) {
              setAdditionalImages(parsed);
            }
          } catch (e) {
            // Ignorer si ce n'est pas un JSON valide
          }
        } else if (Array.isArray(product.additional_images)) {
          setAdditionalImages(product.additional_images);
        }
      }
      
      // Charger les packs si présents
      if (product.packs) {
        if (typeof product.packs === 'string') {
          try {
            const parsed = JSON.parse(product.packs);
            if (Array.isArray(parsed)) {
              setFormData(prev => ({ ...prev, packs: parsed }));
            }
          } catch (e) {
            // Ignorer si ce n'est pas un JSON valide
          }
        } else if (Array.isArray(product.packs)) {
          setFormData(prev => ({ ...prev, packs: product.packs || undefined }));
        }
      }

      // Trouver la catégorie et sous-catégorie sélectionnées
      if (product.category) {
        for (const cat of categories) {
          if (cat.name === product.category) {
            setSelectedCategoryId(cat.id.toString());
            break;
          }
          for (const subCat of cat.sub_categories || []) {
            if (subCat.name === product.category) {
              setSelectedCategoryId(cat.id.toString());
              setSelectedSubCategoryId(subCat.id.toString());
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      // Erreur gérée par l'état error
    } finally {
      setLoadingProduct(false);
    }
  };


  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = t('adminAddProduct.errors.nameRequired');
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = t('adminAddProduct.errors.priceRequired');
    }

    if (!stockUnlimited && (!formData.stock_quantity || formData.stock_quantity < 0)) {
      newErrors.stock_quantity = t('adminAddProduct.errors.stockRequired');
    }

    if (!formData.image_url || formData.image_url === '/images/bottle.png') {
      newErrors.image_url = t('adminAddProduct.errors.imageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Réinitialiser l'erreur pour ce champ
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'radio') {
      setFormData(prev => ({ ...prev, [name]: value === 'yes' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId('');
    
    if (categoryId) {
      const category = categories.find(cat => cat.id.toString() === categoryId);
      if (category) {
        setFormData(prev => ({ ...prev, category: category.name }));
      }
    } else {
      setFormData(prev => ({ ...prev, category: '' }));
    }
  };

  const handleSubCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const subCategoryId = e.target.value;
    setSelectedSubCategoryId(subCategoryId);
    
    if (subCategoryId) {
      const category = categories.find(cat => cat.id.toString() === selectedCategoryId);
      if (category) {
        const subCategory = (category.sub_categories || []).find(sub => sub.id.toString() === subCategoryId);
        if (subCategory) {
          setFormData(prev => ({ ...prev, category: subCategory.name }));
        }
      }
    } else {
      const category = categories.find(cat => cat.id.toString() === selectedCategoryId);
      if (category) {
        setFormData(prev => ({ ...prev, category: category.name }));
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        const errorMsg = t('adminAddProduct.errors.notImage', { name: file.name });
        setErrors(prev => ({ ...prev, image_url: errorMsg }));
        setNotification({ type: 'error', message: errorMsg });
        return;
      }

      try {
        const optimized = await compressImageFile(file, {
          maxWidth: 1600,
          maxHeight: 1600,
          targetBytes: 900_000,
          minQuality: 0.42,
        });

        if (optimized.finalBytes > MAX_FILE_INPUT_BYTES) {
          const errorMsg = t('adminAddProduct.errors.imageTooBig', { name: file.name });
          setErrors(prev => ({ ...prev, image_url: errorMsg }));
          setNotification({ type: 'error', message: errorMsg });
          return;
        }

        setMainImage(optimized.dataUrl);
        setFormData(prev => ({ ...prev, image_url: optimized.dataUrl }));
        setErrors(prev => ({ ...prev, image_url: undefined }));

        if (optimized.finalBytes < optimized.originalBytes) {
          const reduction = Math.round((1 - optimized.finalBytes / optimized.originalBytes) * 100);
          setNotification({
            type: 'info',
            message: `Image optimisée automatiquement (-${reduction}%).`,
          });
        }
      } catch (err) {
        console.error('Erreur compression image principale:', err);
        const errorMsg = 'Impossible de traiter cette image. Essayez un autre fichier.';
        setErrors(prev => ({ ...prev, image_url: errorMsg }));
        setNotification({ type: 'error', message: errorMsg });
      }
    }
  };

  const handleAdditionalImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newImages: string[] = [];
    let optimizedCount = 0;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setNotification({ type: 'error', message: t('adminAddProduct.errors.notImage', { name: file.name }) });
        continue;
      }

      try {
        const optimized = await compressImageFile(file, {
          maxWidth: 1200,
          maxHeight: 1200,
          targetBytes: 480_000,
          minQuality: 0.38,
        });

        if (optimized.finalBytes > MAX_FILE_INPUT_BYTES) {
          setNotification({ type: 'error', message: t('adminAddProduct.errors.imageTooBig', { name: file.name }) });
          continue;
        }

        if (optimized.finalBytes < optimized.originalBytes) {
          optimizedCount += 1;
        }

        newImages.push(optimized.dataUrl);
      } catch (err) {
        console.error('Erreur compression image supplémentaire:', err);
        setNotification({
          type: 'warning',
          message: `Image ignorée (${file.name}) : format invalide.`,
        });
      }
    }

    if (newImages.length > 0) {
      setAdditionalImages(prev => [...prev, ...newImages]);
    }

    if (optimizedCount > 0) {
      setNotification({
        type: 'info',
        message: `${optimizedCount} image(s) supplémentaire(s) optimisée(s) automatiquement.`,
      });
    }
  };

  const removeAdditionalImage = (index: number) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (status: 'draft' | 'published' = 'draft') => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const productData: CreateProductDto = {
        ...formData,
        status,
        stock_quantity: stockUnlimited ? undefined : formData.stock_quantity,
        stock_unlimited: stockUnlimited,
        packs: formData.packs && formData.packs.length > 0 ? formData.packs : undefined,
        // Envoyer additional_images comme tableau, le backend fera le JSON.stringify
        additional_images: additionalImages.length > 0 ? additionalImages : undefined,
        // Envoyer le SKU seulement s'il est renseigné, sinon le backend le générera automatiquement
        sku: formData.sku && formData.sku.trim() !== '' ? formData.sku.trim() : undefined,
      };

      if (productId) {
        // Mode édition
        await productService.update(parseInt(productId), productData);
        setNotification({ type: 'success', message: t('adminAddProduct.success.updated') });
      } else {
        // Mode création
        await productService.create(productData);
        setNotification({ type: 'success', message: status === 'published' ? t('adminAddProduct.success.published') : t('adminAddProduct.success.draft') });
      }
      
      setTimeout(() => {
        navigate('/admin/liste-produits');
      }, 1500);
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      console.error('Détails de l\'erreur:', error?.response?.data);
      
      // Si l'erreur est 401 (Non authentifié), rediriger vers la page de connexion
      if (error?.response?.status === 401) {
        setNotification({ 
          type: 'error', 
          message: t('adminAddProduct.sessionExpired') 
        });
        // Rediriger vers la page de connexion après un court délai
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }
      
      // Message d'erreur plus détaillé
      let errorMessage = t('adminAddProduct.saveError');
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Message spécifique pour les erreurs de base de données
      if (error?.response?.status === 500 && errorMessage.includes('base de données')) {
        errorMessage = t('adminAddProduct.dbError');
      }
      
      setNotification({ type: 'error', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateFinalPrice = () => {
    if (formData.price && formData.reduced_price) {
      return formData.price - formData.reduced_price;
    }
    return formData.price || 0;
  };

  const selectedCategory = useMemo(
    () => categories.find((cat) => cat.id.toString() === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  /** Sous-catégories actives uniquement (liste déroulante) */
  const availableSubCategories = useMemo(
    () => (selectedCategory?.sub_categories || []).filter((s) => s.is_active),
    [selectedCategory]
  );

  if (loadingProduct) {
    return (
    <>

      {notification && (
                <Alert
                  type={notification.type}
                  message={notification.message}
                  onClose={() => setNotification(null)}
                />
              )}
              <div className="table-loading">{t('adminAddProduct.loadingProduct')}</div>
    
    </>
  );
  }

  return (
    <>

    {notification && (
              <Alert
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification(null)}
              />
            )}
            <div className="product-form-container">
              {/* Colonne de gauche - Informations principales */}
              <div className="product-form-column">
                {/* Section 1: Informations de base */}
                <div className="form-section">
                  <h3 className="form-section-title">{t('adminAddProduct.basicInfo')}</h3>
                  <div className="form-group">
                    <label htmlFor="product-name" className="form-label">
                      {t('adminAddProduct.productName')} <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <input
                      type="text"
                      id="product-name"
                      name="name"
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('adminAddProduct.productNamePlaceholder')}
                      required
                    />
                    {errors.name && <div className="error-message">{errors.name}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="product-sku" className="form-label">
                      {t('adminAddProduct.sku')}
                    </label>
                    <input
                      type="text"
                      id="product-sku"
                      name="sku"
                      className="form-input"
                      value={formData.sku || ''}
                      onChange={handleInputChange}
                      placeholder={t('adminAddProduct.skuPlaceholder')}
                    />
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {t('adminAddProduct.skuAuto')}
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="product-description" className="form-label">{t('adminAddProduct.description')}</label>
                    <textarea
                      id="product-description"
                      name="description"
                      className="form-textarea"
                      rows={5}
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={t('adminAddProduct.descriptionPlaceholder')}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="product-more-information" className="form-label">
                      {t('adminAddProduct.moreInformation')}
                    </label>
                    <textarea
                      id="product-more-information"
                      name="more_information"
                      className="form-textarea"
                      rows={6}
                      value={formData.more_information ?? ''}
                      onChange={handleInputChange}
                      placeholder={t('adminAddProduct.moreInformationPlaceholder')}
                    />
                  </div>
                </div>

                {/* Section 2: Tarification */}
                <div className="form-section">
                  <h3 className="form-section-title">{t('adminAddProduct.pricing')}</h3>
                  <div className="form-group">
                    <label htmlFor="product-price" className="form-label">
                      {t('adminAddProduct.price')} <span className="form-label-required">*</span>
                    </label>
                    <div className="input-with-dropdown">
                      <input
                        type="number"
                        id="product-price"
                        name="price"
                        className={`form-input ${errors.price ? 'error' : ''}`}
                        value={formData.price || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, price: value }));
                          if (errors.price) {
                            setErrors(prev => ({ ...prev, price: undefined }));
                          }
                        }}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                      <span className="text-body" style={{ padding: '0 12px' }}>€</span>
                    </div>
                    {errors.price && <div className="error-message">{errors.price}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="reduced-price" className="form-label">{t('adminAddProduct.reducedPrice')}</label>
                    <div className="reduced-price-group">
                      <input
                        type="number"
                        id="reduced-price"
                        name="reduced_price"
                        className="form-input"
                        value={formData.reduced_price || ''}
                        onChange={(e) => {
                          const value = e.target.value ? parseFloat(e.target.value) : undefined;
                          setFormData(prev => ({ ...prev, reduced_price: value }));
                        }}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                      {formData.reduced_price && formData.price && (
                        <span className="price-info">
                          {t('adminAddProduct.salePrice')} = <span id="calculated-price">{calculateFinalPrice().toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> €
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('adminAddProduct.taxesIncluded')}</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="taxes_included"
                          value="yes"
                          checked={formData.taxes_included}
                          onChange={() => setFormData(prev => ({ ...prev, taxes_included: true }))}
                        />
                        <span>{t('adminAddProduct.yes')}</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="taxes_included"
                          value="no"
                          checked={!formData.taxes_included}
                          onChange={() => setFormData(prev => ({ ...prev, taxes_included: false }))}
                        />
                        <span>{t('adminAddProduct.no')}</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Section 3: Packs de traitement */}
                <div className="form-section">
                  <h3 className="form-section-title">{t('adminAddProduct.treatmentPacks')}</h3>
                  <div className="form-group">
                    <label className="form-label">{t('adminAddProduct.addPacks')}</label>
                    <div className="packs-list">
                      {formData.packs?.map((pack, index) => (
                        <div key={pack.id} className="pack-item">
                          <div className="pack-content">
                            <div className="pack-field">
                              <label>{t('adminAddProduct.duration')}</label>
                              <input
                                type="text"
                                value={pack.duration}
                                onChange={(e) => {
                                  const newPacks = [...(formData.packs || [])];
                                  newPacks[index].duration = e.target.value;
                                  setFormData(prev => ({ ...prev, packs: newPacks }));
                                }}
                                placeholder={t('adminAddProduct.durationPlaceholder')}
                                className="form-input"
                              />
                            </div>
                            <div className="pack-field">
                              <label>{t('adminAddProduct.priceEur')}</label>
                              <input
                                type="number"
                                value={pack.price}
                                onChange={(e) => {
                                  const newPacks = [...(formData.packs || [])];
                                  newPacks[index].price = parseFloat(e.target.value) || 0;
                                  setFormData(prev => ({ ...prev, packs: newPacks }));
                                }}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                                className="form-input"
                              />
                            </div>
                            <div className="pack-field">
                              <label>{t('adminAddProduct.badge')}</label>
                              <select
                                value={pack.badge || ''}
                                onChange={(e) => {
                                  const newPacks = [...(formData.packs || [])];
                                  newPacks[index].badge = e.target.value as 'PLUS POPULAIRE' | 'MEILLEURE OFFRE' | null || null;
                                  setFormData(prev => ({ ...prev, packs: newPacks }));
                                }}
                                className="form-select"
                              >
                                <option value="">{t('adminAddProduct.noBadge')}</option>
                                <option value="PLUS POPULAIRE">{t('adminAddProduct.mostPopular')}</option>
                                <option value="MEILLEURE OFFRE">{t('adminAddProduct.bestOffer')}</option>
                              </select>
                            </div>
                            <button
                              type="button"
                              className="btn-remove-pack"
                              onClick={() => {
                                const newPacks = formData.packs?.filter((_, i) => i !== index) || [];
                                setFormData(prev => ({ ...prev, packs: newPacks }));
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="20" height="20">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn-add-pack"
                      onClick={() => {
                        const newPack: ProductPack = {
                          id: Date.now().toString(),
                          duration: '',
                          price: 0,
                          badge: null,
                        };
                        setFormData(prev => ({
                          ...prev,
                          packs: [...(prev.packs || []), newPack],
                        }));
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      {t('adminAddProduct.addPack')}
                    </button>
                  </div>
                </div>

                {/* Section 4: Inventaire */}
                <div className="form-section">
                  <h3 className="form-section-title">{t('adminAddProduct.inventory')}</h3>
                  <div className="form-group">
                    <label htmlFor="stock-quantity" className="form-label">
                      {t('adminAddProduct.stockQuantity')} <span className="form-label-required">*</span>
                    </label>
                    <div className="stock-quantity-group">
                      <input
                        type="number"
                        id="stock-quantity"
                        name="stock_quantity"
                        className={`form-input ${errors.stock_quantity ? 'error' : ''}`}
                        value={formData.stock_quantity || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          setFormData(prev => ({ ...prev, stock_quantity: value }));
                          if (errors.stock_quantity) {
                            setErrors(prev => ({ ...prev, stock_quantity: undefined }));
                          }
                        }}
                        placeholder={t('adminAddProduct.quantityPlaceholder')}
                        min="0"
                        disabled={stockUnlimited}
                        required={!stockUnlimited}
                      />
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          id="stock-unlimited"
                          checked={stockUnlimited}
                          onChange={(e) => {
                            setStockUnlimited(e.target.checked);
                            setFormData(prev => ({ ...prev, stock_unlimited: e.target.checked }));
                            if (errors.stock_quantity) {
                              setErrors(prev => ({ ...prev, stock_quantity: undefined }));
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">{t('adminAddProduct.unlimited')}</span>
                    </div>
                    {errors.stock_quantity && <div className="error-message">{errors.stock_quantity}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="stock-status" className="form-label">{t('adminAddProduct.stockStatus')}</label>
                    <select
                      id="stock-status"
                      name="stock_status"
                      className="form-select"
                      value={formData.stock_status}
                      onChange={handleInputChange}
                    >
                      <option value="in-stock">{t('adminAddProduct.inStock')}</option>
                      <option value="out-of-stock">{t('adminAddProduct.outOfStock')}</option>
                      <option value="pre-order">{t('adminAddProduct.preOrder')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                      />
                      <span>{t('adminAddProduct.featured')}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Colonne de droite - Images et catégories */}
              <div className="product-form-column">
                {/* Section 1: Images du produit */}
                <div className="form-section">
                  <h3 className="form-section-title">
                    {t('adminAddProduct.productImages')} <span className="form-label-required">*</span>
                  </h3>
                  <div className="image-upload-area">
                    {mainImage ? (
                      <div className="main-image-preview">
                        <img src={mainImage} alt={t('adminAddProduct.mainImage')} className="product-main-image" />
                        <div className="image-label">{t('adminAddProduct.mainImage')}</div>
                      </div>
                    ) : (
                      <div className="main-image-preview" style={{ 
                        border: '2px dashed #ddd', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        minHeight: '200px',
                        backgroundColor: '#f9f9f9'
                      }}>
                        <div style={{ textAlign: 'center', color: '#999' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="48" height="48" style={{ margin: '0 auto 12px', opacity: 0.5 }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                          </svg>
                          <div>{t('adminAddProduct.noImage')}</div>
                        </div>
                      </div>
                    )}
                    <div className="image-upload-buttons">
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageUpload}
                      />
                      <button className="btn-browse" onClick={() => imageInputRef.current?.click()} type="button">
                        {mainImage ? t('adminAddProduct.replace') : t('adminAddProduct.browse')}
                      </button>
                      {mainImage && (
                        <button 
                          className="btn-replace" 
                          onClick={() => {
                            setMainImage('');
                            setFormData(prev => ({ ...prev, image_url: '' }));
                          }} 
                          type="button"
                          style={{ marginLeft: '8px', backgroundColor: '#f44336' }}
                        >
                          {t('adminAddProduct.delete')}
                        </button>
                      )}
                    </div>
                    {errors.image_url && <div className="error-message">{errors.image_url}</div>}
                  </div>
                  <div className="additional-images">
                    <div className="form-group">
                      <label className="form-label">{t('adminAddProduct.additionalImages')}</label>
                      <div className="images-grid">
                        {additionalImages.map((img, index) => (
                          <div key={index} className="image-thumbnail">
                            <img src={img} alt={`Thumbnail ${index + 1}`} />
                            <button
                              className="remove-image-btn"
                              onClick={() => removeAdditionalImage(index)}
                              type="button"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                        <div 
                          className="image-thumbnail add-image"
                          onClick={() => additionalImagesInputRef.current?.click()}
                          style={{ cursor: 'pointer' }}
                        >
                          <input
                            ref={additionalImagesInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleAdditionalImagesUpload}
                          />
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="24" height="24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                          </svg>
                          <span>{t('adminAddProduct.addImage')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Catégories et classification */}
                <div className="form-section">
                  <h3 className="form-section-title">{t('adminAddProduct.categoriesClassification')}</h3>
                  <div className="form-group">
                    <label htmlFor="product-category" className="form-label">
                      {t('adminAddProduct.category')} <span className="form-label-required">*</span>
                    </label>
                    {loadingCategories ? (
                      <div style={{ padding: '12px', color: '#666' }}>{t('adminAddProduct.loadingCategories')}</div>
                    ) : (
                      <select
                        id="product-category"
                        name="category"
                        className={`form-select ${errors.category ? 'error' : ''}`}
                        value={selectedCategoryId}
                        onChange={handleCategoryChange}
                        required
                      >
                        <option value="">{t('adminAddProduct.selectCategory')}</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {errors.category && <div className="error-message">{errors.category}</div>}
                  </div>
                  {selectedCategoryId && (
                    <div className="form-group">
                      <label htmlFor="product-subcategory" className="form-label">
                        {t('adminAddProduct.subcategory')}
                      </label>
                      <p className="form-help-text" style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>
                        {t('adminAddProduct.subcategoryHint')}
                      </p>
                      {availableSubCategories.length > 0 ? (
                        <select
                          id="product-subcategory"
                          className="form-select"
                          value={selectedSubCategoryId}
                          onChange={handleSubCategoryChange}
                          aria-label={t('adminAddProduct.subcategory')}
                        >
                          <option value="">{t('adminAddProduct.noSubcategory')}</option>
                          {availableSubCategories.map((subCategory) => (
                            <option key={subCategory.id} value={subCategory.id}>
                              {subCategory.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p
                          id="product-subcategory"
                          className="form-help-text"
                          style={{ margin: 0, padding: '10px 12px', background: '#f5f5f5', borderRadius: 8, fontSize: 13, color: '#555' }}
                          role="status"
                        >
                          {t('adminAddProduct.noSubcategoriesInCategory')}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="product-tag" className="form-label">{t('adminAddProduct.productTag')}</label>
                    <input
                      type="text"
                      id="product-tag"
                      name="tag"
                      className="form-input"
                      value={formData.tag || ''}
                      onChange={handleInputChange}
                      placeholder={t('adminAddProduct.tagPlaceholder')}
                    />
                  </div>
                </div>

                {/* Section 3: Actions */}
                <div className="form-section">
                  <div className="form-actions">
                    <button
                      className="btn-draft"
                      onClick={() => handleSave('draft')}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t('adminAddProduct.saving') : productId ? t('adminAddProduct.update') : t('adminAddProduct.saveDraft')}
                    </button>
                    <button
                      className="btn-publish"
                      onClick={() => handleSave('published')}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t('adminAddProduct.publishing') : productId ? t('adminAddProduct.updateAndPublish') : t('adminAddProduct.publish')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
  
    </>
  );
};
